import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Check, Download, FileText, Zap } from "lucide-react";
import { PageHeader } from "@/components/site/kit";
import { Reveal } from "@/components/motion/primitives";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";
import api from "@/lib/api";
import { toast } from "sonner";

const INSIDE = [
  "Step 1 — Your one goal: the single action your site exists to produce",
  "Step 2 — Your customer: who you're talking to, and what they fear",
  "Step 3 — Your offer, in their words (not industry jargon)",
  "Step 4 — Your proof: reviews, numbers, and assets to gather",
  "Step 5 — Your pages: sketch a sitemap in five boxes",
  "Step 6 — Features: what to build now vs later",
  "Step 7 — Budget & timeline: realistic numbers, no surprises",
];

const inputCls = "w-full bg-secondary px-4 py-3.5 outline-none focus:ring-1 focus:ring-primary text-foreground";

export default function Playbook() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/playbook", form).then((x) => x.data);
      setDone(r);
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="playbook-page">
      <SEO
        title="Free Website Planning Playbook (PDF) — Plan a Website That Wins Customers | Julius Mason"
        description="Download the free 10-page Website Planning Playbook: the 7-step framework I use to plan every client website, with printable worksheets. Instant download."
        path="/free-playbook"
        lang="en"
        keywords="website planning, website checklist, free website playbook, plan a business website, website brief template"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Free Playbook", path: "/free-playbook" }])}
      />
      <PageHeader
        chapter="Free Download / No Catch"
        titleLines={["The Website", "Planning Playbook."]}
        description="The exact 7-step framework I use to plan every client website — with printable worksheets. Fill it in and you'll know what your site needs, what it should cost, and what to do first."
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
          {/* What's inside */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="border border-border bg-card p-8 md:p-10 relative overflow-hidden">
                <BookOpen className="w-10 h-10 text-primary mb-6" />
                <h2 className="font-display text-3xl mb-3">What's inside</h2>
                <p className="text-muted-foreground mb-8 leading-relaxed">
                  Ten pages, seven worksheets, zero fluff. Thirty minutes with this playbook saves weeks of rework —
                  and gives you a project brief any developer would love to receive.
                </p>
                <div className="space-y-3">
                  {INSIDE.map((s, i) => (
                    <div key={s} className="flex gap-3 text-sm items-start" data-testid={`playbook-step-${i}`}>
                      <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground/85">{s}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8 border border-dashed border-primary/40 bg-primary/5 p-4 text-sm text-muted-foreground">
                  <b className="text-foreground">Who it's for:</b> anyone about to build or rebuild a business website —
                  whether you hire me, hire someone else, or DIY. It's yours to keep either way.
                </div>
              </div>
            </Reveal>
          </div>

          {/* Capture form / download */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            {done ? (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="border border-primary/40 bg-primary/5 p-8" data-testid="playbook-success">
                <FileText className="w-10 h-10 text-primary mb-5" />
                <h2 className="font-display text-3xl">It's yours, {form.name.split(" ")[0]}.</h2>
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  Your download is ready below — I've also emailed you the link so you can find it later.
                </p>
                <a href={done.download_url} download data-testid="playbook-download"
                  className="mt-6 inline-flex items-center justify-center gap-2 bg-primary text-white px-7 py-4 font-semibold hover:bg-primary/90 active:scale-95 transition w-full">
                  <Download className="w-5 h-5" /> Download the Playbook (PDF)
                </a>
                <div className="mt-6 border-t border-primary/20 pt-6">
                  <p className="text-sm text-muted-foreground mb-4">Filled it in? The natural next step:</p>
                  <Link to="/pricing" data-testid="playbook-quote-cta" className="inline-flex items-center gap-2 text-primary font-semibold link-underline">
                    Get My Instant Quote <Zap className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="border border-border bg-card p-8 soft-shadow-lg" data-testid="playbook-form">
                <p className="overline text-primary mb-2">Free · Instant</p>
                <h2 className="font-display text-3xl mb-6">Get the Playbook</h2>
                <div className="space-y-5">
                  <div>
                    <label htmlFor="pb-name" className="overline text-muted-foreground block mb-2">Name *</label>
                    <input id="pb-name" required value={form.name} onChange={set("name")} data-testid="playbook-name" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="pb-email" className="overline text-muted-foreground block mb-2">Email *</label>
                    <input id="pb-email" required type="email" value={form.email} onChange={set("email")} data-testid="playbook-email" className={inputCls} />
                  </div>
                </div>
                <button type="submit" disabled={busy} data-testid="playbook-submit"
                  className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-primary text-white px-7 py-4 font-semibold hover:bg-primary/90 active:scale-[0.98] transition disabled:opacity-60">
                  {busy ? "Preparing…" : "Send Me the Playbook"} <Download className="w-4 h-4" />
                </button>
                <p className="mt-4 text-xs text-muted-foreground text-center">
                  Instant download + a copy by email. Occasional useful follow-ups — never spam, unsubscribe anytime.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="mx-auto max-w-2xl px-5">
          <h2 className="font-display text-4xl md:text-5xl">Prefer to skip the homework?</h2>
          <p className="mt-4 text-primary-foreground/85 text-lg">Answer six questions and get an instant project estimate instead.</p>
          <Link to="/pricing" data-testid="playbook-bottom-cta" className="mt-8 inline-flex items-center gap-2 bg-background text-primary px-8 py-4 font-semibold hover:bg-background/90 active:scale-95 transition">
            Get My Instant Quote <Zap className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
